import { useMutation } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { ServerCrash, Trash } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { Toast } from '@/common/components/Toast'
import { useModalProvider } from '@/common/hooks/useModalProvider'
import { api, ApiError } from '@/lib/axios'
import { queryClient } from '@/lib/tanstack'

async function deletePost(postId: string) {
  return api.backend.delete(`/posts/${postId}`)
}

export function useExcludePost() {
  const { t } = useTranslation('panel')
  const { handleModal, toggleLoading } = useModalProvider()

  const { mutateAsync, isPending } = useMutation<
    unknown,
    AxiosError<ApiError>,
    string
  >({
    mutationFn: (postId: string) => deletePost(postId),
    onSuccess: () => {
      toast(Toast, {
        data: {
          type: 'success',
          icon: Trash,
          title: t('news.use_exclude_post.toast_success.title'),
        },
      })
      queryClient.invalidateQueries({
        queryKey: ['/posts/paginate'],
        exact: false,
      })
      queryClient.invalidateQueries({
        queryKey: ['/posts-slider/paginate'],
        exact: false,
      })
      queryClient.invalidateQueries({
        queryKey: ['/posts/paginate/pinned'],
        exact: false,
      })
    },
    onError: (error) => {
      toast(Toast, {
        data: {
          type: 'danger',
          icon: ServerCrash,
          title: t('news.use_exclude_post.toast_error.title'),
          description:
            error?.response?.data?.friendlyMessage ||
            t('news.use_exclude_post.toast_error.description'),
        },
      })
    },
  })

  const handleExcludePost = async (postId: string) => {
    handleModal({
      type: 'danger',
      title: t('news.use_exclude_post.modal.title'),
      description: t('news.use_exclude_post.modal.description'),
      confirmText: t('news.use_exclude_post.modal.button_confirm'),
      onConfirm: async () => {
        toggleLoading()
        await mutateAsync(postId).finally(() => {
          toggleLoading()
        })
      },
    })
  }

  return {
    handleExcludePost,
    isExcludingPost: isPending,
  }
}
